import { useState } from 'react';
import { omit } from 'lodash';
import { useToast } from '@morressier/alembic';
import {
  ISubmissionFormData,
  useFormContext as useSubmissionFormContext
} from 'src/contexts/FormContext';
import { useDraftSubmission } from 'src/api/mutations';
import { generateId } from '@publishing-engine/domain/utils';
import { useKeycloak } from 'src/components/AuthHOC';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DraftResponseProvider } from 'src/contexts/uploadDraftProvider';
import { fetchSubmissionContext } from 'src/api/queries';
import { useQuery } from '@tanstack/react-query';
import { useSubmissionStepsConfig } from '../../../../hooks/useSubmissionStepsConfig';
import { SubmissionLeaveGuardProvider } from './SubmissionUpload/SubmissionLeaveGuard';
import { allowSubmissionNavigationWithoutPrompt } from './SubmissionUpload/SubmissionLeaveGuard';
import {
  FileReplacementNavigationContext,
  type ReplaceableFileField,
  savePendingFileReplacement
} from './SubmissionUpload/fileReplacementNavigation';
import { getSourceFileSetSignature } from './SubmissionUpload/fileSetSignature';

export type FileItem = {
  isUploading?: boolean;
  [key: string]: any;
};

export function checkFileUpload(
  data: Record<string, FileItem | FileItem[]>
): boolean {
  return Object.entries(data).some(([_, value]) => {
    if (Array.isArray(value)) {
      return value.some(item => item?.isUploading === true);
    } else if (typeof value === 'object' && value !== null) {
      return value.isUploading === true;
    }
    return false;
  });
}

const scrollToTop = () => window.scrollTo({ top: 0 });

// to exclude draft, upload, extraction and success step
const SubmissionUpload = () => {
  const { steps } = useSubmissionStepsConfig();
  const totalStepsForAuthor = steps.length - 4;

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [prevStep, setPrevStep] = useState<number>(0);

  const [isMetadataExtracted, setMetadataExtracted] = useState<boolean>(false);
  const [submissionId, setSubmissionId] = useState<string>('');
  const [manuscriptId, setManuscriptId] = useState<string>('');
  const [draftSubmissionId, setDraftSubmissionId] = useState<string>('');
  const toast = useToast();
  const navigate = useNavigate();
  const { currentUser } = useKeycloak();
  const {
    formData: currentFormData,
    consolidatedPdfSourceFileSignature
  } = useSubmissionFormContext();
  const draftSubmissionMutation = useDraftSubmission();
  const { ctxId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const getIsFileUpdated = (formData: ISubmissionFormData) => {
    const hasGeneratedPdfs = Boolean(
      formData.fileSet?.ConsolidatedPdf &&
        formData.fileSet?.AnonymizedConsolidatedPdf
    );
    const hasSourceFileChanges = Boolean(
      hasGeneratedPdfs &&
        consolidatedPdfSourceFileSignature !== null &&
        getSourceFileSetSignature(formData.fileSet) !==
          consolidatedPdfSourceFileSignature
    );

    return (
      formData.isFileUpdated ?? currentFormData.isFileUpdated ?? false
    ) || hasSourceFileChanges;
  };

  const { data } = useQuery({
    queryKey: ['submission-context', ctxId],
    queryFn: () => (ctxId ? fetchSubmissionContext(ctxId) : null)
  });

  const goToNextStep = () => {
    if (currentStep < steps.length) {
      setPrevStep(currentStep);
      setCurrentStep(prev => prev + 1);
      scrollToTop();
    }
  };
  const goToFirstStepManual = () => {
    setPrevStep(currentStep);
    setCurrentStep(3);
    scrollToTop();
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setPrevStep(currentStep);
      setCurrentStep(prev => prev - 1);
      scrollToTop();
    }
  };

  const goToFirstStep = () => {
    setPrevStep(currentStep);
    setCurrentStep(1);
    setSubmissionId('');
    setManuscriptId('');
    setDraftSubmissionId('');
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('draftId');
    setSearchParams(nextSearchParams, { replace: true });
    scrollToTop();
  };

  const saveDraftSubmission = async (
    formData: ISubmissionFormData,
    submissionId?: string
  ) => {
    const { submissionContext, hasRunExtraction } = formData;
    const submission = omit(formData, [
      'submissionContext',
      'hasRunExtraction',
      'isFileUpdated'
    ]);
    const {
      submissionContextId,
      articleWorkflowId,
      articleTypeCategoryId,
      schemaId
    } = submissionContext;

    const response = await draftSubmissionMutation.mutateAsync({
      articleWorkflowId,
      articleTypeCategoryId,
      submissionContextId,
      submissionId: submissionId ? submissionId : generateId(),
      articleType: submission.type,
      draft: { ...submission, schemaId },
      hasRunExtraction,
      isFileUpdated: getIsFileUpdated(formData),
      draftStep: currentStep,
      createdBy: currentUser.id
    });

    if (response.isSuccess) {
      allowSubmissionNavigationWithoutPrompt();
      toast({
        variant: 'success',
        title: 'Draft Saved',
        description:
          'You can continue this Submission later, starting from where you left off.',
        isClosable: true
      });
      navigate('/app/my-submissions');
    } else {
      toast({
        variant: 'error',
        title: 'Draft Save Unsuccessful',
        description:
          'We encountered an issue while saving your submission draft. Please try again.',
        isClosable: true
      });
    }
  };
  const autosaveDraftSubmission = async (
    formData: ISubmissionFormData,
    submissionId?: string,
    draftStep?: number
  ) => {
    const { submissionContext, hasRunExtraction } = formData;
    const submission = omit(formData, [
      'submissionContext',
      'hasRunExtraction',
      'isFileUpdated'
    ]);
    const {
      submissionContextId,
      articleWorkflowId,
      articleTypeCategoryId,
      schemaId
    } = submissionContext;

    const data = {
      articleWorkflowId,
      articleTypeCategoryId,
      submissionContextId,
      submissionId: submissionId ?? draftSubmissionId,
      articleType: submission.type,
      draft: { ...submission, schemaId },
      hasRunExtraction,
      isFileUpdated: getIsFileUpdated(formData),
      draftStep: draftStep ?? currentStep + 1,
      createdBy: currentUser.id
    };
    const response = await draftSubmissionMutation.mutateAsync(data);

    return {
      response,
      data
    };
  };

  if (!data) {
    return <h1>Missing journal information</h1>;
  }

  const step =
    steps[currentStep > steps.length ? steps.length - 1 : currentStep];

  return (
    <DraftResponseProvider>
      {step && (
        <SubmissionLeaveGuardProvider>
          <FileReplacementNavigationContext.Provider
            value={(field: ReplaceableFileField) => {
              const targetStep = steps.findIndex(
                stepItem => stepItem.stepKey === field.stepKey
              );
              if (targetStep < 0) return;

              savePendingFileReplacement(field);
              setCurrentStep(targetStep);
              scrollToTop();
            }}
          >
            <step.Component
              currentStep={currentStep > 2 ? currentStep - 2 : 2}
              prevStep={prevStep}
              totalSteps={totalStepsForAuthor}
              submissionId={submissionId}
              manuscriptId={manuscriptId}
              organizationId={data.organizationId}
              draftSubmissionId={draftSubmissionId}
              isMetadataExtracted={isMetadataExtracted}
              goToNextStep={goToNextStep}
              setSubmissionId={setSubmissionId}
              setManuscriptId={setManuscriptId}
              setDraftSubmissionId={setDraftSubmissionId}
              goToPreviousStep={goToPreviousStep}
              goToFirstStepManual={goToFirstStepManual}
              goToFirstStep={goToFirstStep}
              setMetadataExtracted={setMetadataExtracted}
              saveDraftSubmission={saveDraftSubmission}
              autosaveDraftSubmission={autosaveDraftSubmission}
              goToStep={setCurrentStep}
              journalName={data.data.shortname}
              stepConfigs={step.configs as unknown}
            />
          </FileReplacementNavigationContext.Provider>
        </SubmissionLeaveGuardProvider>
      )}
    </DraftResponseProvider>
  );
};

export default SubmissionUpload;
